import katex from 'katex';
import { rewriteRoots } from '@padcanvas/shared';

/** A `\command` (or a `\;` `\,` `\:` `\!` spacing command), a `^` exponent or a
 *  `_` subscript — never prose. The subscript form skips `____` blanks and
 *  snake_case words. */
const BARE_MATH = /\\(?:[A-Za-z]+|[,;:!])|[A-Za-z0-9)\]}]\^[{(+\-0-9A-Za-z]|(?<![A-Za-z0-9_])[A-Za-z][)\]}]?_(?:\{|[A-Za-z0-9](?![A-Za-z0-9_]))/g;

/**
 * Plain-text intent for an unbraced script: `2^10`, `10^-3`, `e^(x+1)`, `a_12`,
 * `k_obs` mean the whole run is raised or lowered, but KaTeX scripts only the
 * next character (2¹0). Brace those runs; return null when a script is still
 * ambiguous (`e^(x` cut at a space) so the token stays as text.
 */
function braceScripts(token: string): string | null {
  const braced = token
    .replace(/([\^_])\(([^()]*)\)/g, '$1{($2)}')
    .replace(/([\^_])(-?\d+(?:\.\d+)?)/g, '$1{$2}')
    .replace(/_([A-Za-z][A-Za-z0-9]+)/g, '_{$1}');
  return /[\^_][(\-+]/.test(braced) ? null : braced;
}

/**
 * Maths typed without `$…$` — `F^{2}t/m`, `\sqrt{2g\ell(1 - \cos \theta)}`,
 * `v_0` — would otherwise show as raw source. Each such token (whitespace-
 * bounded, extended across spaces only while a `{` is open, since math mode
 * drops the spaces of any prose it swallows) is rendered as math when KaTeX
 * accepts it, and left as text when it doesn't.
 */
export function splitBareMath(text: string): { math: boolean; value: string }[] {
  const parts: { math: boolean; value: string }[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  BARE_MATH.lastIndex = 0;
  while ((m = BARE_MATH.exec(text)) !== null) {
    let start = m.index;
    while (start > last && !/\s/.test(text[start - 1])) start--;
    let end = start;
    let depth = 0;
    while (end < text.length && text[end] !== '\n' && (depth > 0 || !/\s/.test(text[end]))) {
      if (text[end] === '{') depth++;
      else if (text[end] === '}') depth--;
      end++;
    }
    while (end > m.index + m[0].length && /[.,;:!?]/.test(text[end - 1])) end--;
    BARE_MATH.lastIndex = end;
    const token = text.substring(start, end);
    // Links, and literal `–`-style escapes (KaTeX reads `\u` as an accent).
    if (/:\/\/|^www\.|\\u[0-9a-fA-F]{4}/.test(token)) continue;
    // A bare `%` starts a comment in math mode and would hide the rest.
    const braced = braceScripts(token);
    const math = braced === null ? null : rewriteRoots(braced).replace(/(?<!\\)%/g, '\\%');
    if (math === null) continue;
    try {
      katex.renderToString(math, { throwOnError: true });
    } catch {
      continue;
    }
    if (start > last) parts.push({ math: false, value: text.substring(last, start) });
    parts.push({ math: true, value: math });
    last = end;
  }
  if (last < text.length) parts.push({ math: false, value: text.substring(last) });
  return parts;
}
